import axios, { AxiosProgressEvent } from 'axios';
import { AnalysisResult } from '../types/analysis';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL
  : 'http://localhost:8000';

// Configure axios defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes timeout
  withCredentials: true, // Important for CORS with credentials
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add a response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. The server is taking too long to respond.';
    }
    return Promise.reject(error);
  }
);

/**
 * Creates a base result object with default values and handles various response formats
 */
const createBaseResult = (data: any = {}): AnalysisResult => {
  try {
    // Handle case where the response is a string that needs to be parsed
    let responseData = data;
    if (typeof data === 'string') {
      try {
        responseData = JSON.parse(data);
      } catch (e) {
        console.warn('Could not parse response data as JSON, using as-is');
      }
    }
    
    // Extract data from various possible locations in the response
    const analysisResult = responseData.analysis_result || responseData.result || responseData;
    
    // Handle overall_analysis from various possible locations
    let overallAnalysis = '';
    const possibleAnalysisFields = [
      'overall_analysis', 
      'analysis', 
      'analysis_text',
      'summary',
      'analysis_result.overall_analysis',
      'result.overall_analysis'
    ];
    
    for (const field of possibleAnalysisFields) {
      const value = getNestedValue(responseData, field);
      if (value && typeof value === 'string') {
        overallAnalysis = value;
        break;
      }
    }
    
    // Get overall_score with fallbacks
    let overallScore = 0;
    const possibleScoreFields = [
      'overall_score',
      'score',
      'analysis_result.overall_score',
      'result.overall_score'
    ];
    
    for (const field of possibleScoreFields) {
      const value = getNestedValue(responseData, field);
      if (typeof value === 'number') {
        overallScore = value;
        break;
      }
    }
    
    // Process category scores
    let categoryScores: Record<string, number | any> = {};
    const scores = responseData.category_scores || responseData.scores || {};
    
    if (scores && typeof scores === 'object') {
      // Handle both { category: { score: X, reasoning: '...' } } and { category: X } formats
      Object.entries(scores).forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'score' in value) {
          categoryScores[key] = value; // keep full object with reasoning
        } else if (typeof value === 'number') {
          categoryScores[key] = value;
        }
      });
    }
    
    // Process recommendations
    const recommendations = Array.isArray(responseData.recommendations) 
      ? responseData.recommendations 
      : [];
      
    const keyStrengths = Array.isArray(responseData.key_strengths)
      ? responseData.key_strengths
      : [];
      
    const keyWeaknesses = Array.isArray(responseData.key_weaknesses)
      ? responseData.key_weaknesses
      : (Array.isArray(responseData.key_concerns) ? responseData.key_concerns : []);
    
    // Process recommendations by category
    let recommendationsByCategory: Record<string, string[]> = {};
    const recsByCat = responseData.recommendations_by_category || 
                     responseData.category_recommendations ||
                     {};
                     
    if (recsByCat && typeof recsByCat === 'object') {
      Object.entries(recsByCat).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          recommendationsByCategory[key] = value.map(String);
        } else if (value) {
          recommendationsByCategory[key] = [String(value)];
        }
      });
    }
    
    // Create the base result
    const result: AnalysisResult = {
      analysis_timestamp: responseData.analysis_timestamp || new Date().toISOString(),
      model_used: responseData.model_used || 'Unknown',
      overall_score: overallScore,
      category_scores: categoryScores,
      key_strengths: keyStrengths,
      key_weaknesses: keyWeaknesses,
      recommendations: recommendations,
      overall_analysis: overallAnalysis,
      original_filename: responseData.original_filename || responseData.filename || '',
      file_size_bytes: responseData.file_size_bytes || responseData.file_size || 0,
      recommendations_by_category: recommendationsByCategory,
      next_steps: Array.isArray(responseData.next_steps) ? responseData.next_steps : [],
      error: responseData.error || undefined,
      error_details: responseData.error_details || undefined,
      ia_fit_score: responseData.ia_fit_score || undefined,
      ia_fit_breakdown: responseData.ia_fit_breakdown || undefined,
      score_percentile: typeof responseData.score_percentile === 'number'
        ? responseData.score_percentile
        : undefined
    };
    
    return result;
  } catch (error) {
    console.error('Error creating base result:', error);
    
    // Return a minimal valid result in case of errors
    return {
      analysis_timestamp: new Date().toISOString(),
      model_used: 'Unknown',
      overall_score: 0,
      category_scores: {},
      key_strengths: [],
      key_weaknesses: [],
      recommendations: [],
      overall_analysis: 'An error occurred while processing the analysis results.',
      original_filename: '',
      file_size_bytes: 0,
      recommendations_by_category: {},
      next_steps: [],
      error: 'Failed to process analysis results'
    };
  }
};

/**
 * Helper function to safely get nested object properties
 */
const getNestedValue = (obj: any, path: string): any => {
  try {
    return path.split('.').reduce((o, p) => (o || {})[p], obj);
  } catch (e) {
    return undefined;
  }
};

/**
 * Analyze pitch deck text
 * @param text The text content to analyze
 * @returns Analysis result
 */
export const analyzePitchDeckText = async (text: string, email?: string): Promise<AnalysisResult> => {
  try {
    const response = await apiClient.post<AnalysisResult>('/api/analyze', {
      text,
      ...(email && { email }) // Only include email if provided
    });
    return createBaseResult(response.data);
  } catch (error) {
    let errorMessage = 'Failed to analyze text';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.detail || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    throw new Error(`API error: ${errorMessage}`);
  }
};

/** Analysis pipeline stages reported while a job is running. */
export type AnalysisStage = 'queued' | 'extracting' | 'analyzing' | 'finalizing';

interface JobStatus {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  stage: AnalysisStage | null;
  error: string | null;
  result: any | null;
}

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 8 * 60 * 1000; // give long PDF analyses room

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Uploads a file for analysis and polls the analysis job until it finishes.
 *
 * The upload returns immediately with a job id; the analysis runs server-side
 * as a background job and progress stages are surfaced via `onStage`.
 *
 * @throws Error with a user-displayable message on upload or analysis failure
 */
export const uploadAndAnalyzeFile = async (
  file: File,
  email: string,
  country: string,
  privacyConsent: boolean,
  consentStorage?: boolean,
  consentImprovement?: boolean,
  industry?: string,
  stage?: string,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  onStage?: (stage: AnalysisStage) => void
): Promise<AnalysisResult> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('email', email);
  formData.append('country', country);
  formData.append('privacy_consent', privacyConsent.toString());

  // When comprehensive consent is given, both storage and improvement are allowed
  formData.append('consent_storage', (consentStorage !== undefined ? consentStorage : privacyConsent).toString());
  formData.append('consent_improvement', (consentImprovement !== undefined ? consentImprovement : privacyConsent).toString());

  if (industry) {
    formData.append('industry', industry);
  }
  if (stage) {
    formData.append('stage', stage);
  }

  // 1. Upload — returns a job id immediately
  let jobId: string;
  try {
    const response = await axios({
      method: 'post',
      url: `${API_BASE_URL}/api/upload/`,
      data: formData,
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json',
      },
      onUploadProgress,
      timeout: 120000, // upload only — analysis happens asynchronously
    });
    jobId = response.data?.job_id;
    if (!jobId) {
      throw new Error('The server did not return an analysis job id.');
    }
  } catch (error) {
    throw new Error(uploadErrorMessage(error));
  }
  onStage?.('queued');

  // 2. Poll the job until it completes or fails
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let consecutivePollFailures = 0;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    let job: JobStatus;
    try {
      const { data } = await apiClient.get<JobStatus>(`/api/jobs/${jobId}`);
      job = data;
      consecutivePollFailures = 0;
    } catch {
      // Tolerate transient network blips while polling
      consecutivePollFailures += 1;
      if (consecutivePollFailures >= 5) {
        throw new Error('Lost connection to the server while analyzing. Please try again.');
      }
      continue;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'The analysis failed. Please try again.');
    }
    if (job.status === 'completed' && job.result) {
      return createBaseResult({
        ...job.result,
        original_filename: job.result.filename || file.name,
        file_size_bytes: job.result.file_size_bytes || file.size,
      });
    }
    if (job.stage) {
      onStage?.(job.stage);
    }
  }

  throw new Error('The analysis is taking longer than expected. Please try again.');
};

const uploadErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (error.response?.status === 413) {
      return 'File is too large. Please upload a file smaller than 100MB.';
    }
    if (error.response?.status === 400) {
      return typeof detail === 'string' ? detail : 'Invalid request. Please check the file and try again.';
    }
    if (error.code === 'ECONNABORTED') {
      return 'The upload timed out. Please check your connection and try again.';
    }
    if (!error.response) {
      return 'No response from the server. Please check if the backend is running and accessible.';
    }
    return typeof detail === 'string' ? detail : 'An internal server error occurred. Please try again later.';
  }
  return error instanceof Error ? error.message : 'Failed to upload the file.';
};

